import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, ImagePlus, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "@/components/ui/sonner";
import { imgUrl } from "@/pages/admin/PropertiesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-lg border border-line bg-warm/60 px-3.5 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft mb-2";

const EMPTY = { name: "", designation: "", photo: "", bio: "", phone: "", email: "", published: false };

export default function TeamForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(EMPTY);
  const [order, setOrder] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("rk_admin_token")}` });

  useEffect(() => {
    if (!isEdit) return;
    if (!localStorage.getItem("rk_admin_token")) return navigate("/admin/login");
    axios
      .get(`${API}/admin/team`, { headers: headers() })
      .then(({ data }) => {
        const m = data.find((x) => x.id === id);
        if (!m) {
          toast.error("Team member not found.");
          return navigate("/admin?tab=team");
        }
        const { id: _i, created_at: _c, updated_at: _u, order: _o, ...rest } = m;
        setForm({ ...EMPTY, ...rest });
        setOrder(m.order || 0);
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/uploads`, fd, { headers: headers() });
      setForm((f) => ({ ...f, photo: data.path }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (publish) => {
    if (form.name.trim().length < 2) return toast.error("Name is required.");
    setSaving(true);
    const payload = { ...form, name: form.name.trim(), order, published: publish ?? form.published };
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/team/${id}`, payload, { headers: headers() });
      } else {
        await axios.post(`${API}/admin/team`, payload, { headers: headers() });
      }
      toast.success(payload.published ? "Member published — live on the website." : "Member saved as draft.");
      navigate("/admin?tab=team");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="min-h-screen bg-warm flex items-center justify-center text-ink-soft"><Loader2 className="animate-spin" size={28} /></div>;

  return (
    <div className="min-h-screen bg-warm" data-testid="team-form-page">
      <header className="bg-navy sticky top-0 z-40">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <Link to="/admin?tab=team" data-testid="team-form-logo"><Logo dark /></Link>
          <Link to="/admin?tab=team" className="text-[13px] text-white/60 hover:text-gold transition-colors inline-flex items-center gap-1.5" data-testid="team-form-back">
            <ArrowLeft size={14} /> Back to Team
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-navy">{isEdit ? "Edit Team Member" : "Add Team Member"}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">Published members appear in the Our Management Team section on the homepage.</p>

        <div className="mt-8 bg-white rounded-xl border border-line p-6 sm:p-8 space-y-6">
          <div>
            <label className={labelCls}>Profile Photo</label>
            <div className="flex items-start gap-4">
              {form.photo ? (
                <div className="relative w-40 aspect-[4/4.4] rounded-lg overflow-hidden border border-line">
                  <img src={imgUrl(form.photo)} alt="Profile" className="w-full h-full object-cover" />
                  <button onClick={() => setForm((f) => ({ ...f, photo: "" }))} className="absolute top-2 right-2 h-7 w-7 rounded-md bg-navy/80 text-white flex items-center justify-center hover:bg-red-600 transition-colors" aria-label="Remove photo" data-testid="tf-photo-remove">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="w-40 aspect-[4/4.4] rounded-lg border-2 border-dashed border-line hover:border-gold flex flex-col items-center justify-center gap-2 cursor-pointer text-ink-soft hover:text-navy transition-colors" data-testid="tf-photo-upload">
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                  <span className="text-[11px] font-semibold">{uploading ? "Uploading..." : "Upload Photo"}</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} data-testid="tf-photo-input" />
                </label>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Name*</label>
              <input className={inputCls} placeholder="e.g. Rajesh Kumar" value={form.name} onChange={set("name")} data-testid="tf-name" />
            </div>
            <div>
              <label className={labelCls}>Designation</label>
              <input className={inputCls} placeholder="e.g. Founder & Director" value={form.designation} onChange={set("designation")} data-testid="tf-designation" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Phone</label>
              <input className={inputCls} placeholder="+91 98XXX XXXXX" value={form.phone} onChange={set("phone")} data-testid="tf-phone" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input className={inputCls} placeholder="name@rkrealestate.com" value={form.email} onChange={set("email")} data-testid="tf-email" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Bio</label>
            <textarea rows={4} placeholder="A short introduction — experience, focus areas..." value={form.bio} onChange={set("bio")}
              className="w-full rounded-lg border border-line bg-warm/60 px-3.5 py-3 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition resize-none"
              data-testid="tf-bio" />
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => save(false)} disabled={saving}
            className="flex-1 border border-navy/25 text-navy hover:border-gold transition-colors rounded-lg px-6 py-3.5 text-[15px] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="tf-save-draft">
            {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit && form.published ? "Save (Keep Published)" : "Save as Draft"}
          </button>
          <button onClick={() => save(true)} disabled={saving}
            className="flex-1 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3.5 text-[15px] font-semibold shadow-md shadow-gold/25 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="tf-save-publish">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save & Publish"}
          </button>
        </div>
      </main>
    </div>
  );
}
